"use client";

import React from "react";
import { motion } from "framer-motion";
import { Search, MapPin, TrendingUp, Target, CircleCheck as CheckCircle2, ArrowRight, ChartBar as BarChart3, ShieldCheck, Building2, MousePointerClick } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const marketInsights = [{
  id: "insight-1",
  title: "High-Intent Local Demand",
  stat: "78%",
  description: "Of local mobile searches result in an offline booking or direct enquiry within 24 hours.",
  badge: "Consumer Behavior"
}, {
  id: "insight-2",
  title: "Google Map Pack Dominance",
  stat: "46%",
  description: "Of all Google searches have local intent, with top 3 map positions taking over 60% of total clicks.",
  badge: "Market Opportunity"
}, {
  id: "insight-3",
  title: "Organic vs Directory Preference",
  stat: "82%",
  description: "Of Bolton customers bypass paid directory listings in favor of verified organic local business results.",
  badge: "Trust Factor"
}];
const behaviorPillars = [{
  id: "pillar-1",
  iconName: "Search",
  title: "Hyper-Local Keyword Patterns",
  subtitle: "How Bolton Customers Search",
  description: "Customers in Bolton rarely search with generic terms. They combine high-intent phrases with specific local modifiers, neighborhood names, and 'near me' triggers when ready to buy."
}, {
  id: "pillar-2",
  iconName: "Building2",
  title: "Commercial & B2B Opportunity",
  subtitle: "Regional Authority Building",
  description: "Local service providers and regional B2B companies in Bolton often lack technical SEO structure, creating a massive opening for rapid rank acquisition with direct consultant intervention."
}, {
  id: "pillar-3",
  iconName: "MousePointerClick",
  title: "Zero-Click Search Adaptation",
  subtitle: "Featured Snippets & Maps",
  description: "With Google displaying rich local answers directly on SERPs, optimization must focus on structured schema, local entity signals, and direct call triggers that convert instantly."
}];
const methodologySteps = [{
  id: "step-1",
  title: "Localized Technical Audit",
  text: "I inspect local schema markups, NAP consistency, and geo-targeted landing page structures to ensure search engines accurately interpret your exact service perimeter."
}, {
  id: "step-2",
  title: "Competitor Gap Identification",
  text: "I analyze established Bolton competitors to uncover untapped search queries, weak backlink profiles, and content gaps you can immediately capitalize on."
}, {
  id: "step-3",
  title: "Conversion-Focused Optimisation",
  text: "I align your local search visibility with direct user action, ensuring every visitor lands on a fast, trustworthy, high-converting page designed to generate leads."
}];
export default function Localsearch() {
  return <section id="localsearchmarketanalysis" data-nav="light" className="py-20 lg:py-28 bg-background text-foreground relative overflow-hidden">
      {}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-secondary/30 rounded-full blur-3xl pointer-events-none -z-10" />
      <div className="absolute bottom-10 left-10 w-80 h-80 bg-accent/10 rounded-full blur-3xl pointer-events-none -z-10" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }}>
            <Badge variant="outline" className="mb-4 border-accent/40 bg-secondary/50 text-foreground px-3 py-1 text-xs font-semibold tracking-wide">
              <MapPin className="w-3.5 h-3.5 mr-1 text-accent" /> Bolton &amp; Regional Market Analysis
            </Badge>
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-primary tracking-tight">
            Local Search Landscape &amp; Market Opportunity
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="mt-4 text-lg text-muted-foreground leading-relaxed">
            When I evaluate search behavior across Bolton and Greater Manchester, the pattern is unmistakable: high-intent local buyers are actively searching for trusted specialists, yet most business websites fail to capture this demand.
          </motion.p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {marketInsights.map((insight, i) => <div data-index={i} key={insight.id}>
              <motion.div initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="h-full">
                <Card className="h-full border border-border/80 bg-card hover:border-primary/30 transition-[border-color,box-shadow] duration-300 shadow-sm hover:shadow-md">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="secondary" className="bg-secondary text-secondary-foreground text-xs font-medium">
                        {insight.badge}
                      </Badge>
                      <BarChart3 className="w-4 h-4 text-accent" />
                    </div>
                    <div className="text-4xl font-extrabold text-primary font-serif tracking-tight">
                      {insight.stat}
                    </div>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <CardTitle className="text-lg font-semibold text-foreground mb-2">
                      {insight.title}
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                      {insight.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>
            </div>)}
        </div>

        {}
        <div className="mb-20">
          <div className="bg-muted/40 border border-border/60 rounded-2xl p-6 sm:p-10">
            <div className="max-w-2xl mb-10">
              <h3 className="text-2xl font-bold font-serif text-primary mb-3">
                Bolton Search Behavior &amp; Demand Breakdown
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                Generic SEO templates treat every location identically. Here is how customer intent in Bolton actually functions and why a tailored, direct-consultant approach generates superior commercial returns.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {behaviorPillars.map((pillar, i) => {
              const IconComponent = pillar.iconName === "Search" ? Search : pillar.iconName === "Building2" ? Building2 : MousePointerClick;
              return <div data-index={i} key={pillar.id}>
                    <motion.div initial={{
                  opacity: 0,
                  y: 20
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.5,
                  delay: i * 0.12
                }} className="bg-card rounded-xl p-6 border border-border/60 h-full flex flex-col justify-between">
                      <div>
                        <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-4">
                          <IconComponent className="w-5 h-5" />
                        </div>
                        <span className="text-xs font-semibold tracking-wider text-accent uppercase block mb-1">
                          {pillar.subtitle}
                        </span>
                        <h4 className="text-lg font-bold text-foreground mb-3">
                          {pillar.title}
                        </h4>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {pillar.description}
                        </p>
                      </div>
                    </motion.div>
                  </div>;
            })}
            </div>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center mb-16">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-accent text-sm font-semibold mb-3">
              <ShieldCheck className="w-4 h-4" /> Direct Senior Expertise
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold font-serif text-primary mb-4 leading-tight">
              Why You Work Directly With Me—Not an Agency Account Manager
            </h3>
            <p className="text-muted-foreground mb-6 leading-relaxed">
              When you hire YE Consultancy, you work directly with me, Yassine Essaadi. I personally audit your local search ecosystem, craft your bespoke ranking strategy, and execute the technical work required to dominate Bolton search engine result pages.
            </p>

            <div className="space-y-4 mb-8">
              {methodologySteps.map((step, i) => <div data-index={i} key={step.id} className="flex gap-4 items-start">
                  <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-1">
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <h5 className="text-base font-semibold text-foreground">
                      {step.title}
                    </h5>
                    <p className="text-sm text-muted-foreground leading-relaxed mt-0.5">
                      {step.text}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="flex flex-wrap gap-4 items-center">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
                <Link to="/ContactPage">
                  Book a Free Local SEO Consultation <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-border hover:bg-accent/10">
                <Link to="/LocalSEOPage">
                  View Local SEO Services
                </Link>
              </Button>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="lg:col-span-5">
            <div className="relative rounded-2xl bg-gradient-to-br from-primary to-primary/80 text-primary-foreground p-8 shadow-xl overflow-hidden">
              <div className="absolute top-0 right-0 -mr-12 -mt-12 w-48 h-48 bg-accent/20 rounded-full blur-2xl pointer-events-none" />
              
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center font-bold text-lg border border-white/20">
                    YE
                  </div>
                  <div>
                    <h4 className="font-semibold text-white text-base">Yassine Essaadi</h4>
                    <p className="text-xs text-white/80">Independent SEO Consultant</p>
                  </div>
                </div>

                <Separator className="bg-white/20 mb-6" />

                <blockquote className="text-sm sm:text-base italic leading-relaxed text-white/90 mb-6">
                  &quot;My goal for Bolton businesses is simple: turn search visibility into reliable, sustainable customer acquisition. I don&apos;t hide behind vanity metrics or automated reports—you receive transparent reporting and direct access to me.&quot;
                </blockquote>

                <div className="space-y-2 text-xs text-white/80">
                  <div className="flex items-center gap-2">
                    <Target className="w-3.5 h-3.5 text-accent" />
                    <span>Tailored Local Strategy, No Generic Templates</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-3.5 h-3.5 text-accent" />
                    <span>Focus on Revenue &amp; Qualified Inquiries</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}